import React, { useState, useEffect, useRef } from 'react';

interface AutocompleteInputProps<T> {
  id: string;
  label: string;
  placeholder?: string;
  items: T[];
  value: number | string | undefined;
  onChange: (value: number | string) => void;
  getItemValue: (item: T) => number | string;
  getItemLabel: (item: T) => string;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
  noItemsMessage?: string;
  minCharsToShow?: number;
}

function AutocompleteInput<T>({
  id,
  label,
  placeholder = 'Type to search...',
  items,
  value,
  onChange,
  getItemValue,
  getItemLabel,
  disabled = false,
  error,
  required = false,
  className = '',
  noItemsMessage = 'No items found',
  minCharsToShow = 1,
}: AutocompleteInputProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Find selected item and set initial search term
  useEffect(() => {
    const selectedItem = items.find(item => getItemValue(item) === value);
    if (selectedItem) {
      setSearchTerm(getItemLabel(selectedItem));
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value, items, getItemValue, getItemLabel]);

  // Filter items based on search term
  useEffect(() => {
    if (searchTerm.length >= minCharsToShow) {
      const lowercaseTerm = searchTerm.toLowerCase();
      const filtered = items.filter(item =>
        getItemLabel(item).toLowerCase().includes(lowercaseTerm)
      );
      setFilteredItems(filtered);
      setIsOpen(isUserTyping && filtered.length > 0);
    } else {
      setFilteredItems([]);
      setIsOpen(false);
    }
  }, [searchTerm, items, getItemLabel, minCharsToShow]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Reset search term if no value is selected
        if (!value) {
          setSearchTerm('');
        } else {
          const selectedItem = items.find(item => getItemValue(item) === value);
          if (selectedItem) {
            setSearchTerm(getItemLabel(selectedItem));
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [value, items, getItemValue, getItemLabel]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHighlightedIndex(-1);
    setIsUserTyping(true);
    // Clear selection if user is typing
    if (value) {
      const selectedItem = items.find(item => getItemValue(item) === value);
      if (selectedItem && getItemLabel(selectedItem) !== newValue) {
        onChange('');
      }
    }
  };

  const handleSelect = (item: T) => {
    onChange(getItemValue(item));
    setSearchTerm(getItemLabel(item));
    setIsOpen(false);
    setHighlightedIndex(-1);
    setIsUserTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredItems.length === 0) {
      if (e.key === 'ArrowDown' && searchTerm.length >= minCharsToShow) {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredItems.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          handleSelect(filteredItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    if (!value && searchTerm.length >= minCharsToShow && filteredItems.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className={`autocomplete-input ${className}`} ref={wrapperRef}>
      <label htmlFor={id} className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>

      <div className="position-relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          className={`form-control ${error ? 'is-invalid' : ''}`}
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          disabled={disabled}
          autoComplete="off"
        />

        {isOpen && filteredItems.length > 0 && (
          <div
            className="dropdown-menu show position-absolute w-100 mt-1"
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1050,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            {filteredItems.map((item, index) => {
              const itemValue = getItemValue(item);
              const isHighlighted = index === highlightedIndex;
              const isSelected = itemValue === value;

              return (
                <button
                  key={itemValue}
                  type="button"
                  className={`dropdown-item ${isHighlighted ? 'bg-light' : ''} ${isSelected ? 'active' : ''}`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{
                    cursor: 'pointer',
                    background: isSelected ? 'var(--primary)' : isHighlighted ? '#f8f9fa' : undefined,
                    color: isSelected ? 'white' : undefined
                  }}
                >
                  {getItemLabel(item)}
                </button>
              );
            })}
          </div>
        )}

        {isOpen && searchTerm.length >= minCharsToShow && filteredItems.length === 0 && (
          <div
            className="dropdown-menu show position-absolute w-100 mt-1"
            style={{
              zIndex: 1050,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div className="dropdown-item text-muted text-center">
              {noItemsMessage}
            </div>
          </div>
        )}
      </div>

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

export default AutocompleteInput;